<template>
  <div class="app-alert" :class="`app-alert--${theme}`" :style="themeVars" role="alert">
    <i v-if="showIcon" class="app-alert__icon" :class="resolvedIcon"></i>

    <div class="app-alert__body">
      <div v-if="title" class="app-alert__title">{{ title }}</div>
      <div class="app-alert__text"><slot /></div>
    </div>

    <button
      v-if="dismissible"
      type="button"
      class="app-alert__close"
      aria-label="Dismiss"
      @click="$emit('dismiss')"
    >
      <i class="fa-regular fa-xmark"></i>
    </button>
  </div>
</template>

<script>
/**
 * Inline alert / notice banner.
 *
 * Themes mirror the app-button semantic set (soft tint background + accent
 * icon/border). Each theme has a sensible default icon; override with `icon`
 * (auto-prefixed `fa-regular` unless it already carries a weight) or hide it
 * with `:show-icon="false"`.
 *
 * Props:
 *   theme        — info | blue | success | warning | danger | neutral
 *   title        — optional bold heading above the slotted message
 *   icon         — override the default FA icon
 *   show-icon    — toggle the leading icon (default true)
 *   dismissible  — render a × that emits `dismiss`
 *
 * `info` is teal. For a plain informational notice in the app's blue —
 * the same #1f73c1 used by app-badge, app-table and app-empty-state —
 * reach for `blue`.
 */
const THEMES = {
  info:    { accent: '#0ea596', bg: '#e2f5f2', border: '#b9e6df', text: '#0b5f57', icon: 'fa-circle-info' },
  blue:    { accent: '#1f73c1', bg: '#e9f2fb', border: '#c3dcf1', text: '#195aa0', icon: 'fa-circle-info' },
  success: { accent: '#2da44e', bg: '#e9f6ee', border: '#bfe5cb', text: '#1e7e34', icon: 'fa-circle-check' },
  warning: { accent: '#c4801c', bg: '#f8f0e2', border: '#ecd9b3', text: '#8a5a12', icon: 'fa-triangle-exclamation' },
  danger:  { accent: '#cf222e', bg: '#fbe9ea', border: '#f2c4c7', text: '#a11b24', icon: 'fa-circle-exclamation' },
  neutral: { accent: '#6c7a89', bg: '#eef1f4', border: '#dbe0e6', text: '#3b4453', icon: 'fa-circle-info' },
}

export default {
  name: 'AppAlert',

  props: {
    theme: {
      type: String,
      default: 'info',
      validator: (v) => Object.keys(THEMES).includes(v),
    },
    title:    { type: String, default: '' },
    icon:     { type: String, default: '' },
    showIcon: { type: Boolean, default: true },
    dismissible: { type: Boolean, default: false },
  },

  emits: ['dismiss'],

  computed: {
    resolvedIcon() {
      const fallback = (THEMES[this.theme] || THEMES.info).icon
      const name = this.icon || fallback
      return name.includes(' ') ? name : `fa-regular ${name}`
    },

    themeVars() {
      const t = THEMES[this.theme] || THEMES.info
      return {
        '--alert-accent': t.accent,
        '--alert-bg': t.bg,
        '--alert-border': t.border,
        '--alert-text': t.text,
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.app-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--alert-border);
  border-radius: 10px;
  background: var(--alert-bg);
  color: var(--alert-text);
  font-size: 13px;
  line-height: 1.45;

  &__icon {
    flex-shrink: 0;
    margin-top: 1px;
    font-size: 15px;
    color: var(--alert-accent);
  }

  &__body {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__title {
    font-weight: 600;
    margin-bottom: 2px;
  }

  &__text {
    word-break: break-word;
  }

  &__close {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: var(--alert-text);
    opacity: 0.6;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    line-height: 1;
    transition: opacity 140ms ease;

    &:hover { opacity: 1; }
  }
}
</style>
